import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  message,
  Modal,
  notification,
  Row,
  Spin,
  Image,
  Input 
} from "antd";
import dayjs from "dayjs";
import { formatDate, DISPLAY_FORMAT } from "../../../helpers/dateHelper";
import queryString from "query-string";
import { useAuthContext } from "../../providers/AuthProvider";
import { useSytContext } from "../../providers/SytProvider";
import APIClient from "../../../helpers/ApiClient";
import TicketHotel from "./TicketHotel";
import TicketSidebar from "../TicketSidebar";
import LogoImg from "../../../assets/images/logos/trip-logo.png";
import "./HotelTicketScreen.scss";
import {
  applyHotelReviewPanByNameMatch,
  buildHotelVoucherPanFieldErrors,
  clearHotelReviewPanFromOtherRooms,
  exceedsHotelReviewSamePanRoomLimit,
  guestHasPan,
  getGuestDisplayName,
  getHotelGuestRoomNumber,
  getHotelReviewPrimaryPanRoomIndex,
  getHotelReviewRoomPanDisplay,
  getHotelTicketRoomCount,
  groupHotelTicketGuestsByRoom,
  isHotelAdultGuest,
  HOTEL_REVIEW_MAX_ROOMS_PER_PAN,
  isMultiRoomPanRequiredForHotelVoucher,
  isHotelCancelApiSuccess,
  buildHotelTicketAfterCancelPatch,
  getEffectiveHotelBookingStatus,
  HOTEL_CANCELLATION_OTHER_REASON,
  HOTEL_CANCELLATION_REASON_OPTIONS,
  isHotelPaidVoucheredForRefund,
  resolveHotelCancelRefundAmount,
  serializeHotelCancelRemarksForApi,
  showHotelTicketMultiRoomPanSection,
  shouldShowHotelTicketSidebar,
  validateHotelTicketVoucherPan,
} from "./hotelhelper";
import { isMasterMemberAgent } from "../../../pages/Hotels/hotelMasterMember";
import HotelBookingProcessingOverlay from "../../../pages/Hotels/HotelBookingProcessingOverlay/HotelBookingProcessingOverlay";
import {
  cacheHotelTicket,
  clearHotelPendingFlow,
  getCachedHotelTicket,
  getHotelPendingFlow,
} from "../../../pages/Hotels/hotelTicketCache";
// import { PDFViewer } from "@react-pdf/renderer";
// import HotelDoc from "../../PdfDocuments/Hotel/HotelDoc";

const { Panel } = Collapse;
const { TextArea } = Input;
const dateFormat = "DD MMM YYYY";
const HotelTicketScreen = ({ mode }) => {
  const {
    user,
    isLogin: { agent },
  } = useAuthContext();
  const { agentLogo, logo } = useSytContext();
  const location = useLocation();
  const [modalVisible, setModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const ticketSearchParams = queryString.parse(window.location.search);
  const BASE = process.env.REACT_APP_BASE_URL;
  const [logoImage, setLogoImage] = useState(logo);
  const [invoiceAgentLogo, setInvoiceAgentLogo] = useState(null);
  const [ticketData, setTicketData] = useState({});
  const [loadingTicket, setLoadingTicket] = useState(true);
  const [cmsFareRules, setCmsFareRules] = useState({});
  const [ cancelRemarks, setCancelationRemarks] = useState('');
  const [cancelReasons, setCancelReasons] = useState([]);
  const pendingCancelMetaRef = useRef(null);
  const [samePanForAllRooms, setSamePanForAllRooms] = useState(false);
  const [panFieldErrors, setPanFieldErrors] = useState({});
  const [verifiedPanRoomIndex, setVerifiedPanRoomIndex] = useState(null);
  const [refundAlertText, setRefundAlertText] = useState("");
  const [processingFlow, setProcessingFlow] = useState(null);

  const syncVerifiedPanRoomIndex = (data = {}) => {
    const grouped = groupHotelTicketGuestsByRoom(data);
    const hasPan = grouped.some((room) =>
      (room?.paxInfoList ?? []).some((pax) => guestHasPan(pax)),
    );
    setVerifiedPanRoomIndex(
      hasPan ? getHotelReviewPrimaryPanRoomIndex(grouped) : null,
    );
  };

  const syncTicketGuestsFromGrouped = (
    flatGuests = [],
    groupedRooms = [],
    { samePanForAll = false, roomIndex = 0 } = {},
  ) => {
    if (samePanForAll) {
      const primaryIdx = getHotelReviewPrimaryPanRoomIndex(groupedRooms);
      const panHolder = (groupedRooms[primaryIdx]?.paxInfoList ?? []).find(
        (pax) => guestHasPan(pax),
      );
      const sharedPan = String(panHolder?.pan ?? panHolder?.Pan ?? "")
        .trim()
        .toUpperCase();
      if (!sharedPan) {
        return flatGuests;
      }
      // Tripjack v3: same PAN on every pax across all rooms.
      return flatGuests.map((guest) => ({
        ...guest,
        Pan: sharedPan,
        pan: sharedPan,
      }));
    }

    const targetRoom = groupedRooms[roomIndex];
    const roomPan =
      String(
        (targetRoom?.paxInfoList ?? []).find((pax) => guestHasPan(pax))?.pan ??
          (targetRoom?.paxInfoList ?? []).find((pax) => guestHasPan(pax))?.Pan ??
          "",
      )
        .trim()
        .toUpperCase() ||
      getHotelReviewRoomPanDisplay(groupedRooms, roomIndex);

    return flatGuests.map((guest) => {
      const roomNo = getHotelGuestRoomNumber(guest, 1);
      if (roomNo !== roomIndex + 1) {
        return guest;
      }
      if (roomPan) {
        return { ...guest, Pan: roomPan, pan: roomPan };
      }
      const next = { ...guest };
      delete next.Pan;
      delete next.pan;
      return next;
    });
  };

  const showVoucherMultiRoomPan =
    Boolean(agent) &&
    showHotelTicketMultiRoomPanSection(ticketData, {
      membership: user?.Membership,
    });

  const notifyPanError = (text) => {
    message.error({
      content: text,
      duration: 8,
      style: { zIndex: 10050 },
    });
    notification.error({
      message: "PAN Required",
      description: text,
      duration: 10,
      placement: "top",
      style: { zIndex: 10050 },
    });
  };

  const validateTicketVoucherPanBeforePay = () => {
    if (isMasterMemberAgent(user?.Role?.RoleId, user?.Membership)) {
      return true;
    }
    if (!isMultiRoomPanRequiredForHotelVoucher(ticketData)) {
      return true;
    }
    const result = validateHotelTicketVoucherPan(ticketData, {
      samePanForAll: samePanForAllRooms,
      membership: user?.Membership,
    });
    if (result.ok) {
      setPanFieldErrors({});
      return true;
    }
    const fieldErrors = buildHotelVoucherPanFieldErrors(ticketData, {
      samePanForAll: samePanForAllRooms,
    });
    setPanFieldErrors(fieldErrors);
    notifyPanError(result.message);
    const firstRoomIndex = Object.keys(fieldErrors)
      .map((key) => Number(key))
      .filter((n) => Number.isFinite(n))
      .sort((a, b) => a - b)[0];
    if (Number.isFinite(firstRoomIndex)) {
      setTimeout(() => {
        document
          .getElementById(`ticket-room-pan-${firstRoomIndex}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 150);
    }
    return false;
  };

  const handleTicketPanVerified = (panNumber, panMeta, roomIndex) => {
    const roomCount = getHotelTicketRoomCount(ticketData);
    if (exceedsHotelReviewSamePanRoomLimit(roomCount, samePanForAllRooms)) {
      notifyPanError(
        `One PAN number can be used for maximum ${HOTEL_REVIEW_MAX_ROOMS_PER_PAN} rooms only.`,
      );
      return;
    }

    const panName =
      String(panMeta?.pan_returned_name ?? "").trim() ||
      `${panMeta?.first_name ?? ""} ${panMeta?.last_name ?? ""}`.trim();

    const grouped = groupHotelTicketGuestsByRoom(ticketData);
    const updatedGrouped = applyHotelReviewPanByNameMatch(
      grouped,
      panNumber,
      panMeta,
      {
        samePanForAll: samePanForAllRooms,
        roomIndex,
        panName,
      },
    );

    if (!updatedGrouped) {
      notifyPanError(
        "Name not matched with pan no. PAN holder must be a guest in this room.",
      );
      setPanFieldErrors((prev) => ({
        ...prev,
        [String(roomIndex)]: "Name not matched with pan no.",
      }));
      return;
    }

    setPanFieldErrors((prev) => {
      const next = { ...prev };
      delete next[String(roomIndex)];
      if (samePanForAllRooms) {
        Object.keys(next).forEach((key) => delete next[key]);
      }
      return next;
    });

    const updatedGuests = syncTicketGuestsFromGrouped(
      ticketData.guests ?? [],
      updatedGrouped,
      { samePanForAll: samePanForAllRooms, roomIndex },
    );
    setVerifiedPanRoomIndex(roomIndex);
    setTicketData({ ...ticketData, guests: updatedGuests });
    message.success("PAN verified and applied to all guests in the room.");
  };

  const handleSamePanForAllRoomsChange = (checked) => {
    const roomCount = getHotelTicketRoomCount(ticketData);
    if (checked && exceedsHotelReviewSamePanRoomLimit(roomCount, true)) {
      notifyPanError(
        `One PAN number can be used for maximum ${HOTEL_REVIEW_MAX_ROOMS_PER_PAN} rooms only. Please verify PAN separately for each room.`,
      );
      return;
    }
    setSamePanForAllRooms(checked);
    const grouped = groupHotelTicketGuestsByRoom(ticketData);
    if (checked) {
      const primaryIdx =
        verifiedPanRoomIndex ?? getHotelReviewPrimaryPanRoomIndex(grouped);
      const sharedPan = getHotelReviewRoomPanDisplay(grouped, primaryIdx);
      // Copy verified PAN onto lead adult of every room (Tripjack needs per-room lead PAN).
      const propagated =
        sharedPan &&
        applyHotelReviewPanByNameMatch(
          grouped,
          sharedPan,
          {},
          {
            samePanForAll: true,
            roomIndex: primaryIdx,
            matchAnyAdultInRoom: true,
          },
        );
      setTicketData({
        ...ticketData,
        guests: syncTicketGuestsFromGrouped(
          ticketData.guests ?? [],
          propagated || grouped,
          { samePanForAll: true, roomIndex: primaryIdx },
        ),
      });
      setVerifiedPanRoomIndex(primaryIdx);
      setPanFieldErrors({});
    } else {
      const keepRoomIndex =
        verifiedPanRoomIndex ?? getHotelReviewPrimaryPanRoomIndex(grouped);
      const updatedGrouped = clearHotelReviewPanFromOtherRooms(
        grouped,
        keepRoomIndex,
      );
      setTicketData({
        ...ticketData,
        guests: syncTicketGuestsFromGrouped(
          ticketData.guests ?? [],
          updatedGrouped,
          { samePanForAll: false, roomIndex: keepRoomIndex },
        ),
      });
      setVerifiedPanRoomIndex(keepRoomIndex);
      setPanFieldErrors({});
    }
  };

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  const formatRefundInr = (amount) =>
    `₹ ${Number(amount ?? 0).toLocaleString("en-IN")}`;

  const normalizeLoadedHotelTicket = (data = {}, prev = {}, options = {}) => {
    const { preserveLocalCancel = false } = options;
    const normalized = { ...data };
    const refundAmt = Number(normalized?.refundAmount ?? 0);
    const pgPaid = Number(normalized?.pgStatus) === 1;
    const serverCancelled =
      Number(normalized?.cancellationStatus) === 6 ||
      (pgPaid && refundAmt > 0);

    if (serverCancelled && Number(normalized?.BookingStatus) !== 6) {
      normalized.BookingStatus = 6;
      normalized.cancellable = false;
      if (!normalized.cancellationStatus) {
        normalized.cancellationStatus = 6;
      }
    }

    if (preserveLocalCancel && Number(prev?.BookingStatus) === 6) {
      normalized.BookingStatus = 6;
      normalized.cancellationStatus =
        normalized.cancellationStatus ?? prev.cancellationStatus ?? 6;
      normalized.cancellable = false;
      normalized.refundAmount =
        normalized.refundAmount ?? prev.refundAmount ?? 0;
      normalized.walletRefundAmount =
        normalized.walletRefundAmount ?? prev.walletRefundAmount ?? 0;
      normalized.walletRefunded =
        normalized.walletRefunded ?? prev.walletRefunded ?? false;
      normalized.cancelationRemarks =
        normalized.cancelationRemarks ?? prev.cancelationRemarks ?? "";
    }

    return normalized;
  };

  const applyLoadedTicketData = (data = {}, options = {}) => {
    setTicketData((prev) => {
      let normalized = normalizeLoadedHotelTicket(data, prev, options);
      // Confirmed ticket: if any guest has verified PAN, show it on all pax (v3).
      const guests = Array.isArray(normalized?.guests) ? normalized.guests : [];
      const sharedPan = guests
        .map((g) =>
          String(g?.Pan ?? g?.pan ?? g?.PAN ?? "")
            .trim()
            .toUpperCase(),
        )
        .find((p) => /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(p));
      if (sharedPan && guests.length > 0) {
        const needsStamp = guests.some(
          (g) =>
            String(g?.Pan ?? g?.pan ?? "")
              .trim()
              .toUpperCase() !== sharedPan,
        );
        if (needsStamp) {
          normalized = {
            ...normalized,
            guests: guests.map((g) => ({
              ...g,
              Pan: sharedPan,
              pan: sharedPan,
            })),
          };
        }
      }
      syncVerifiedPanRoomIndex(normalized);
      return normalized;
    });
  };

  const fetchTicketDetails = (
    ref,
    { silent = false, preserveLocalCancel = false, retryCount = 0 } = {},
  ) => {
    if (!ref) {
      if (!silent) {
        setLoadingTicket(false);
      }
      return;
    }
    if (!silent) {
      setLoadingTicket(true);
    }
    APIClient.get("hotels-v2/hotelDetails/" + ref)
      .then((res) => {
        const okStatus =
          res?.status >= 200 && res?.status < 300 && res?.data != null;
        if (okStatus && Object.keys(res.data).length > 0) {
          const incomingStatus = Number(
            res?.data?.BookingStatus ?? res?.data?.Status ?? 0,
          );
          const cachedNow = getCachedHotelTicket(ref);
          const cachedStatus = Number(
            cachedNow?.BookingStatus ?? cachedNow?.Status ?? 0,
          );
          const voucheredCached = cachedStatus === 11 || cachedStatus === 3;
          const staleBlocked =
            incomingStatus === 2 ||
            incomingStatus === 1 ||
            incomingStatus === 10;
          if (
            staleBlocked &&
            (voucheredCached || getHotelPendingFlow(ref) === "voucher") &&
            retryCount < 10
          ) {
            window.setTimeout(() => {
              fetchTicketDetails(ref, {
                silent: true,
                preserveLocalCancel: true,
                retryCount: retryCount + 1,
              });
            }, 1200);
            if (!silent) {
              setLoadingTicket(false);
            }
            return;
          }
          cacheHotelTicket(ref, res.data);
          if (
            !(
              getHotelPendingFlow(ref) === "voucher" &&
              (incomingStatus === 2 || incomingStatus === 10)
            )
          ) {
            clearHotelPendingFlow(ref);
            setProcessingFlow(null);
          }
          if (mode === "USER") {
            const ticketUserId = Number(res?.data?.UserId ?? 0);
            const sessionUserId = Number(user?.UserID ?? user?.UserId ?? 1);
            const ticketAgentId = String(
              res?.data?.agentDetails?.AgentID ??
                res?.data?.AgentID ??
                "",
            ).trim();
            const sessionAgentId = String(
              user?.AgentID ?? user?.agentDetails?.AgentID ?? "",
            ).trim();
            // Strict === failed when API sent number and auth stored string ("1239" !== 1239).
            const sameUser =
              ticketUserId > 0 &&
              sessionUserId > 0 &&
              ticketUserId === sessionUserId;
            const sameAgent =
              ticketAgentId &&
              sessionAgentId &&
              ticketAgentId.toUpperCase() === sessionAgentId.toUpperCase();
            if (sameUser || sameAgent) {
              applyLoadedTicketData(res.data, { preserveLocalCancel });
            } else if (ticketUserId > 0 && sessionUserId > 0) {
              setTicketData({});
              if (!silent) {
                message.warning(
                  `Ticket ${ref} is under ${ticketAgentId || `UserId ${ticketUserId}`}, not your login (${sessionAgentId || sessionUserId}). Switch account or open from Admin.`,
                  8,
                );
              }
            } else {
              applyLoadedTicketData(res.data, { preserveLocalCancel });
            }
          } else {
            applyLoadedTicketData(res.data, { preserveLocalCancel });
          }
        } else if (!silent) {
          setTicketData({});
        }
        if (!silent) {
          setLoadingTicket(false);
        }
      })
      .catch((error) => {
        if (!silent) {
          setTicketData({});
          setLoadingTicket(false);
        }
      });
  };

  useEffect(() => {
    if (agent && agentLogo) {
      setLogoImage(agentLogo);
    }
  }, [agent, agentLogo]);

  useEffect(() => {
    const userId = Number(user?.UserID ?? user?.userId ?? 0);
    if (!agent || !userId) {
      setInvoiceAgentLogo(null);
      return undefined;
    }

    let active = true;

    const toLogoUrl = (path) => {
      const raw = String(path || "").trim();
      if (!raw) return "";
      if (raw.startsWith("data:") || raw.startsWith("http")) return raw;
      return BASE + raw.replace(/^\.\//, "").replace(/^\//, "");
    };

    const embedLogo = (path) => {
      const url = toLogoUrl(path);
      if (!url || !active) {
        if (active) setInvoiceAgentLogo(null);
        return;
      }
      fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error("logo fetch failed");
          return response.blob();
        })
        .then(async (blob) => {
          if (!active || !blob || blob.size < 32) return;
          // Uploaded "jpg" files are sometimes WebP. The invoice PDF only
          // embeds real PNG/JPEG, so redraw through the browser first.
          const bitmap = await createImageBitmap(blob);
          const canvas = document.createElement("canvas");
          canvas.width = bitmap.width;
          canvas.height = bitmap.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(bitmap, 0, 0);
          bitmap.close?.();
          const pngBlob = await new Promise((resolve, reject) => {
            canvas.toBlob((result) => {
              if (result) resolve(result);
              else reject(new Error("logo conversion failed"));
            }, "image/png");
          });
          if (active) setInvoiceAgentLogo(pngBlob);
        })
        .catch(() => {
          if (active) setInvoiceAgentLogo(null);
        });
    };

    APIClient.get("admin/agentBanners")
      .then((res) => {
        if (!active) return;
        const images = res?.data?.images || res?.data?.data?.images || [];
        const mine = (Array.isArray(images) ? images : []).filter(
          (item) => Number(item?.UserId ?? item?.userId) === userId,
        );
        const latest = mine[mine.length - 1] || mine[0];
        embedLogo(latest?.path || "");
      })
      .catch(() => {
        if (active) setInvoiceAgentLogo(null);
      });

    return () => {
      active = false;
    };
  }, [agent, user?.UserID, user?.userId, BASE]);

  const cancelTicket = () => {
    setCancelReasons([]);
    setCancelationRemarks("");
    pendingCancelMetaRef.current = null;
    setModalVisible(true);
  };

  const closeCancelModal = () => {
    setModalVisible(false);
    setCancelReasons([]);
    setCancelationRemarks("");
    pendingCancelMetaRef.current = null;
  };

  const resolveVoucheredRefundAmount = (ticket = ticketData, resData = {}) =>
    resolveHotelCancelRefundAmount(ticket, resData);

  const refreshAgentWalletBalance = () => {
    window.dispatchEvent(new Event("syt:refresh-agent-wallet"));
  };

  const showRefundSuccessNotice = (refundAmt, refNumber = "") => {
    const amount = Number(refundAmt) || 0;
    if (!(amount > 0)) {
      return;
    }
    const refundText = `Your amount is refunded: ${formatRefundInr(amount)}`;
    const ref = String(refNumber || ticketData?.RefNumber || "").trim();
    if (ref) {
      sessionStorage.setItem(`hotel-wallet-refund-notified-${ref}`, "1");
    }
    setRefundAlertText(refundText);
  };

  const showCancelSuccessMessage = (res) => {
    message.destroy("hotel-cancel-progress");
    const cancelData = res?.data ?? {};

    const refundAmt = resolveVoucheredRefundAmount(ticketData, cancelData);
    const walletRefunded = Boolean(cancelData?.walletRefunded);
    const walletRefundAmt = Number(cancelData?.walletRefundAmount ?? 0);
    const pgPaid = Number(ticketData?.pgStatus) === 1;
    const wasVoucheredPaid = isHotelPaidVoucheredForRefund(ticketData);
    // Refund popup only for VOUCHERED tickets where wallet was actually credited.
    // Unpaid Hold (status 10) cancel must not show "amount is refunded".
    const creditedRefundAmt =
      wasVoucheredPaid &&
      pgPaid &&
      walletRefunded &&
      (walletRefundAmt > 0 || refundAmt > 0)
        ? walletRefundAmt > 0
          ? walletRefundAmt
          : refundAmt
        : 0;

    setTicketData((prev) =>
      buildHotelTicketAfterCancelPatch(
        prev,
        cancelData,
        pendingCancelMetaRef.current ?? {},
      ),
    );
    setModalVisible(false);
    setLoading(false);
    setCancelationRemarks("");
    setCancelReasons([]);
    pendingCancelMetaRef.current = null;

    const refNumber = String(
      ticketData?.RefNumber ?? cancelData?.RefNumber ?? "",
    ).trim();
    window.setTimeout(() => {
      if (creditedRefundAmt > 0) {
        showRefundSuccessNotice(creditedRefundAmt, refNumber);
      } else if (
        wasVoucheredPaid &&
        pgPaid &&
        Number(refundAmt) > 0 &&
        !walletRefunded
      ) {
        notification.warning({
          message: "Cancellation Successful",
          description:
            "Ticket cancelled. Wallet refund is processing — balance should update shortly. If not, refresh the page or contact support.",
          duration: 12,
          placement: "top",
          style: { zIndex: 10060 },
        });
      }
    }, 200);
    if (!(creditedRefundAmt > 0)) {
      message.success({
        content: "Hotel booking cancelled successfully.",
        duration: 8,
        style: { zIndex: 10060 },
      });
    }
    refreshAgentWalletBalance();
    // Keep local CANCELLED patch — silent reload was reverting status to VOUCHERED.
  };

  const pendingHotelCancelStatuses = new Set([
    "cancellationrequest",
    "cancellationpending",
    "cancellationinprogress",
  ]);

  const pollHotelCancelStatus = (data, attempt = 0) => {
    const maxAttempts = 12;
    const delayMs = 2000;

    APIClient.post("hotels-v2/hotelCancelStatus/", data)
      .then((res) => {
        const bookingStatus = String(res.data?.bookingStatus ?? "").toLowerCase();
        if (
          isHotelCancelApiSuccess(res) &&
          !pendingHotelCancelStatuses.has(bookingStatus)
        ) {
          showCancelSuccessMessage(res);
          getCmsFareRules(3);
          return;
        }
        if (
          pendingHotelCancelStatuses.has(bookingStatus) &&
          attempt < maxAttempts
        ) {
          window.setTimeout(
            () => pollHotelCancelStatus(data, attempt + 1),
            delayMs,
          );
          return;
        }
        if (isHotelCancelApiSuccess(res)) {
          showCancelSuccessMessage(res);
          getCmsFareRules(3);
          return;
        }
        setLoading(false);
        if (Number(res?.status) === 400) {
          if (Array.isArray(res.data) && res.data.length > 0) {
            const detail = res.data.find((err) => err?.errorDetail)?.errorDetail;
            if (detail) {
              message.error({ content: detail, style: { zIndex: 10050 } });
            }
          }
          setModalVisible(false);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
      });
  };

  const recoverCancelAfterSlowResponse = (refNumber) => {
    const ref = String(refNumber ?? ticketData?.RefNumber ?? "").trim();
    if (!ref) {
      setLoading(false);
      message.error({
        content: "Cancellation timed out. Please refresh the page and check ticket status.",
        style: { zIndex: 10050 },
      });
      return;
    }
    APIClient.get(`hotels-v2/hotelDetails/${ref}`)
      .then((res) => {
        const okStatus =
          res?.status >= 200 && res?.status < 300 && res?.data != null;
        if (!okStatus || Object.keys(res.data).length === 0) {
          setLoading(false);
          message.error({
            content: "Cancellation timed out. Please refresh the page and check ticket status.",
            style: { zIndex: 10050 },
          });
          return;
        }
        cacheHotelTicket(ref, res.data);
        applyLoadedTicketData(res.data, { preserveLocalCancel: true });
        if (getEffectiveHotelBookingStatus(res.data) === 6) {
          closeCancelModal();
          setLoading(false);
          message.success({
            content: "Hotel booking cancelled successfully.",
            duration: 8,
            style: { zIndex: 10060 },
          });
          refreshAgentWalletBalance();
          getCmsFareRules(3);
          return;
        }
        setLoading(false);
        message.error({
          content: "Cancellation could not be completed. Please try again.",
          style: { zIndex: 10050 },
        });
      })
      .catch(() => {
        setLoading(false);
        message.error({
          content: "Cancellation timed out. Please refresh the page and check ticket status.",
          style: { zIndex: 10050 },
        });
      });
  };

  const submitCancelForm = (val) => {
    setLoading(true);

    if (ticketData.traceId == "" && ticketData.RefNumber == "") {
      return;
    }
    if (!Array.isArray(cancelReasons) || cancelReasons.length === 0) {
      message.error({
        content: "Please select at least one reason for cancellation.",
        style: { zIndex: 10050 },
      });
      setLoading(false);
      return;
    }
    const hasOtherReason = cancelReasons.includes(HOTEL_CANCELLATION_OTHER_REASON);
    if (hasOtherReason && cancelRemarks.trim().length < 5) {
      message.error({
        content:
          "Please specify remarks (at least 5 characters) when selecting Other.",
        style: { zIndex: 10050 },
      });
      setLoading(false);
      return;
    }
    const cancelationRemarksPayload = serializeHotelCancelRemarksForApi(
      cancelReasons,
      cancelRemarks,
    );
    pendingCancelMetaRef.current = { cancelationRemarks: cancelationRemarksPayload };
    let data = {
      traceId: ticketData.traceId,
      RefNumber: ticketData.RefNumber,
      cancelRemarks: cancelRemarks.trim(),
      cancellationReasons: cancelReasons,
    };

    APIClient.post("hotels-v2/hotelCancel/", data)
      .then((res) => {
        if (isHotelCancelApiSuccess(res)) {
          const bookingStatus = String(res.data?.bookingStatus ?? "");
          // Portal already cancelled + refunded — close modal even if Tripjack still pending.
          const portalDone =
            res.data?.portalCancelled === true ||
            res.data?.walletRefunded === true;
          if (
            portalDone &&
            [
              "CancellationRequest",
              "CancellationPending",
              "CancellationInProgress",
            ].includes(bookingStatus)
          ) {
            message.destroy("hotel-cancel-progress");
            showCancelSuccessMessage({
              ...res,
              data: { ...res.data, bookingStatus: "Cancelled" },
            });
            return;
          }
          if (
            [
              "CancellationRequest",
              "CancellationPending",
              "CancellationInProgress",
            ].includes(bookingStatus)
          ) {
            message.loading({
              content: "Processing cancellation...",
              key: "hotel-cancel-progress",
              duration: 0,
              style: { zIndex: 10060, marginTop: 72 },
            });
            pollHotelCancelStatus(data);
          } else {
            message.destroy("hotel-cancel-progress");
            showCancelSuccessMessage(res);
          }
        } else if (Number(res?.status) === 400) {
          message.destroy("hotel-cancel-progress");
          setLoading(false);
          if (Array.isArray(res.data) && res.data.length > 0) {
            const detail = res.data.find((err) => err?.errorDetail)?.errorDetail;
            if (detail) {
              message.error({ content: detail, style: { zIndex: 10050 } });
            }
            setModalVisible(false);
          }
        } else if (Number(res?.status) === 408) {
          message.destroy("hotel-cancel-progress");
          recoverCancelAfterSlowResponse(ticketData.RefNumber);
        } else {
          message.destroy("hotel-cancel-progress");
          setLoading(false);
          message.error({
            content: res?.message || "Cancellation failed. Please try again.",
            style: { zIndex: 10050 },
          });
        }
      })
      .catch((error) => {
        message.destroy("hotel-cancel-progress");
        recoverCancelAfterSlowResponse(ticketData.RefNumber);
        console.error(error);
      });
  };

  const getTicketDetails = () => {
    fetchTicketDetails(ticketSearchParams.ref);
  };

  useEffect(() => {
    const ref = String(ticketSearchParams.ref ?? "").trim();
    if (!ref) {
      setLoadingTicket(false);
      return undefined;
    }

    const routeFlow = location.state?.hotelProcessingFlow;
    const pendingFlow = getHotelPendingFlow(ref);
    const resolvedFlow =
      routeFlow === "hold" || routeFlow === "voucher"
        ? routeFlow
        : pendingFlow === "hold" || pendingFlow === "voucher"
          ? pendingFlow
          : null;
    if (resolvedFlow) {
      setProcessingFlow(resolvedFlow);
    }

    const routePrefetch = location.state?.prefetchedTicket;
    const cachedTicket =
      routePrefetch && typeof routePrefetch === "object"
        ? routePrefetch
        : getCachedHotelTicket(ref);
    const hasCachedTicket =
      cachedTicket &&
      typeof cachedTicket === "object" &&
      Object.keys(cachedTicket).length > 0;

    if (hasCachedTicket) {
      applyLoadedTicketData(cachedTicket);
      setLoadingTicket(false);
      cacheHotelTicket(ref, cachedTicket);
    }

    fetchTicketDetails(ref, { silent: hasCachedTicket });

    const retryTimer = window.setTimeout(() => {
      if (getHotelPendingFlow(ref)) {
        fetchTicketDetails(ref, { silent: true, preserveLocalCancel: true });
      }
    }, 2500);

    return () => window.clearTimeout(retryTimer);
  }, [ticketSearchParams.ref]);

  useEffect(() => {
    if (!refundAlertText) {
      return undefined;
    }
    const timer = window.setTimeout(() => setRefundAlertText(""), 15000);
    return () => window.clearTimeout(timer);
  }, [refundAlertText]);
  const getCmsFareRules = (serviceType) => {
    APIClient.get(`admin/getFareRulesbyserviceType/${serviceType}`)
      .then((res) => {
        if (res.status == 200) {
          setCmsFareRules(res.data);
        } else {
          setCmsFareRules({});
        }
      })
      .catch((e) => {
        setCmsFareRules({});
      });
  };
  const handleCancelRemarks = (e) => {
    let Remarks = e.target.value;
    setCancelationRemarks(Remarks);
  };

  const handleCancelReasonToggle = (reason, checked) => {
    if (checked) {
      setCancelReasons([reason]);
      if (reason !== HOTEL_CANCELLATION_OTHER_REASON) {
        setCancelationRemarks("");
      }
      return;
    }

    setCancelReasons([]);
    setCancelationRemarks("");
  };

  const visibleCancelReasons =
    cancelReasons.length > 0
      ? HOTEL_CANCELLATION_REASON_OPTIONS.filter((reason) =>
          cancelReasons.includes(reason),
        )
      : HOTEL_CANCELLATION_REASON_OPTIONS;
  return (
    <div className="flight-ticket-collapse">
      {processingFlow && loadingTicket ? (
        <HotelBookingProcessingOverlay flow={processingFlow} />
      ) : null}
      <div className="d-flex justify-content-between">
        <div className="print-agentlogo">
          {logoImage ? (
            <Image
              className="printImageStyle"
              src={BASE + logoImage.substring(1)}
            ></Image>
          ) : (
            <Image className="printImageStyle" src={LogoImg}></Image>
          )}
        </div>
        <div className="print-agentlogo">
          {ticketData?.agentDetails ? (
            <div>
              <p className="mb-0 text-right">
                {ticketData?.agentDetails?.AgencyName}{" "}
              </p>
              <p className="mb-0 text-right">
                {ticketData?.agentDetails?.Address1}{" "}
                {ticketData?.agentDetails?.Address2}
              </p>
              {ticketData?.agentDetails?.City ||
              ticketData?.agentDetails?.State ? (
                <p className="mb-0 text-right">
                  {ticketData?.agentDetails?.City}{" "}
                  {ticketData?.agentDetails?.State}
                </p>
              ) : null}

              <p className="mb-0 text-right">
                Email: {ticketData?.agentDetails?.Email}
              </p>
              {ticketData?.agentDetails?.Phone ? (
                <p className="mb-0 text-right">
                  Contact No.: {ticketData?.agentDetails?.Phone}
                </p>
              ) : null}
            </div>
          ) : ticketData?.adminDetails?.length > 0 ? (
            <div>
              <p className="mb-0 text-right">
                {ticketData?.adminDetails[0]?.Address}{" "}
              </p>
              <p className="mb-0 text-right">
                {ticketData?.adminDetails[0]?.CityName}{" "}
                {ticketData?.adminDetails[0]?.StateName}
              </p>
              <p className="mb-0 text-right">
                {ticketData?.adminDetails[0]?.CountryName}{" "}
                {ticketData?.adminDetails[0]?.PostalCode}
              </p>
            </div>
          ) : null}
        </div>
      </div>

      <Card>
        <div className="fligh-ticket-container">
          {loadingTicket ? (
            processingFlow ? null : (
            <div style={{ textAlign: "center" }} className="flight-ticket">
              <Spin indicator={antIcon} tip="Loading..." />
            </div>
            )
          ) : ticketData && Object.keys(ticketData).length > 0 ? (
            // [11,10].includes(ticketData.BookingStatus) ? (
            <div className="flight-ticket">
              {/* <PDFViewer>
                <HotelDoc ticketData={ticketData} cmsFareRules={cmsFareRules} />
              </PDFViewer> */}
              <Row gutter={[32, 16]} className="ticket-row">
                <Col
                  md={shouldShowHotelTicketSidebar(ticketData) ? 18 : 24}
                  xs={24}
                  className="ticket-coloum"
                >
                  {/* <Collapse
                    bordered={true}
                    className="collapsed-data"
                    defaultActiveKey={["1"]}
                    accordion={true}
                  >
                    <Panel key="1">
                      <OldTicketHotel
                        ticketData={ticketData}
                       
                        
                      />
                    </Panel>
                  </Collapse> */}
                  <TicketHotel
                    ticketData={ticketData}
                    cmsFareRules={cmsFareRules}
                    showVoucherMultiRoomPan={showVoucherMultiRoomPan}
                    samePanForAllRooms={samePanForAllRooms}
                    verifiedPanRoomIndex={verifiedPanRoomIndex}
                    onSamePanForAllRoomsChange={handleSamePanForAllRoomsChange}
                    onTicketPanVerified={handleTicketPanVerified}
                    panFieldErrors={panFieldErrors}
                    onPanFieldTouched={(roomIndex) =>
                      setPanFieldErrors((prev) => {
                        const next = { ...prev };
                        delete next[String(roomIndex)];
                        return next;
                      })
                    }
                  />
                </Col>

                {shouldShowHotelTicketSidebar(ticketData) ? (
                <Col xs={24} md={6} className="tic-info-flight">
                  <div className="web-tic-info">
                    <TicketSidebar
                      ticketData={ticketData}
                      ticketSearchParams={ticketSearchParams}
                       setTicketData={setTicketData}
                      type="Hotel"
                      ticketMode={mode}
                      onCancelTicket={cancelTicket}
                      getTicketDetails={getTicketDetails}
                      cmsFareRules={cmsFareRules}
                      logoImage={logoImage}
                      invoiceLogo={invoiceAgentLogo}
                      samePanForAllRooms={samePanForAllRooms}
                      validateHotelVoucherPanBeforePay={
                        validateTicketVoucherPanBeforePay
                      }
                    />
                  </div>

                  <div className="mobile-tic-info">
                    <Collapse accordion>
                      <Panel header="Manage Tickets" key="1">
                        <TicketSidebar
                          ticketData={ticketData}
                           setTicketData={setTicketData}
                          ticketSearchParams={ticketSearchParams}
                          type="Hotel"
                          ticketMode={mode}
                          onCancelTicket={cancelTicket}
                          getTicketDetails={getTicketDetails}
                          cmsFareRules={cmsFareRules}
                          logoImage={logoImage}
                          invoiceLogo={invoiceAgentLogo}
                          samePanForAllRooms={samePanForAllRooms}
                          validateHotelVoucherPanBeforePay={
                            validateTicketVoucherPanBeforePay
                          }
                        />
                      </Panel>
                    </Collapse>
                  </div>
                </Col>
                ) : null}
              </Row>
            </div>
          ) : (
            <p>No Ticket Found</p>
          )}
        </div>
      </Card>

      {refundAlertText ? (
        <div
          style={{
            position: "fixed",
            top: 72,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10070,
            width: "min(520px, calc(100vw - 32px))",
          }}
        >
          <Alert
            message="Ticket Cancelled"
            description={refundAlertText}
            type="success"
            showIcon
            closable
            onClose={() => setRefundAlertText("")}
          />
        </div>
      ) : null}

      <Modal
        title="Cancel Hotel"
        className="promo-modal-header hotel-cancel-modal hotel-ticket-modal"
        closeIcon={<CloseOutlined className="hotel-ticket-modal__close-icon" />}
        open={modalVisible}
        onOk={closeCancelModal}
        onCancel={closeCancelModal}
        zIndex={1000}
        footer={
          cancelReasons.length > 0
            ? [
                <div key="cancel-hotel-footer" className="hotel-cancel-modal__footer">
                  <Button
                    key="cancel-hotel"
                    type="primary"
                    onClick={submitCancelForm}
                    loading={loading}
                  >
                    Cancel Hotel
                  </Button>
                </div>,
              ]
            : null
        }
        width={"600px"}
      >
        {ticketData && Object.keys(ticketData).length > 0 &&
        [11, 10, 3].includes(ticketData.BookingStatus) && (

          <div className="wrapper">
            <p>
              <b>Reference No</b> {ticketData.RefNumber}
            </p>
            <p>
              <b>Hotel Name :</b> {ticketData.HotelName}
            </p>

            <p>
              <b>Check In Date:</b> {" "}
              {formatDate(ticketData.CheckInDate, dateFormat)}
            </p>
            <p>
              <b> Check Out Date:</b> {" "}
              {formatDate(ticketData.CheckOutDate, dateFormat)}
            </p>
            <p className="mb-2"><b>Reason for Cancellation</b><sup className="text-danger">*</sup></p>
            <div className="d-flex flex-column" style={{ gap: 8 }}>
              {visibleCancelReasons.map((reason) => (
                <Checkbox
                  key={reason}
                  checked={cancelReasons.includes(reason)}
                  onChange={(e) =>
                    handleCancelReasonToggle(reason, e.target.checked)
                  }
                >
                  {reason}
                </Checkbox>
              ))}
            </div>
            {cancelReasons.includes(HOTEL_CANCELLATION_OTHER_REASON) ? (
              <>
                <p className="mt-3 mb-1">
                  <b>Remarks</b>
                  <sup className="text-danger">*</sup>
                </p>
                <TextArea
                  rows={4}
                  value={cancelRemarks}
                  onChange={handleCancelRemarks}
                  placeholder="Please Enter Remarks"
                  maxLength={250}
                />
              </>
            ) : null}
          </div>
        )}

      </Modal>
    </div>
  );
};
export default HotelTicketScreen;
